import { useEffect } from 'react';
import { Link } from 'wouter';
import { ArrowLeft, Download } from 'lucide-react';

const BASE = import.meta.env.BASE_URL;

const studies = [
  {
    id: 'helmet',
    index: '01',
    name: 'Bike Helmet',
    subtitle: 'Road cycling shell',
    alt: 'Gradient wireframe mesh of a road cycling helmet with long ventilation channels',
    note: 'A low, elongated shell swept from brow to a dropped aero tail. Seven long vents cut clean through, each with an inner wall, leaving sculpted ribs between them. The underside stays open and shows its thickness.',
    specs: [['Shell', 'Swept arches · tail drop'], ['Vents', '7 through-channels'], ['Underside', 'Open · rim + liner edge']],
  },
  {
    id: 'gear',
    index: '02',
    name: 'Gear',
    subtitle: 'Spur, 20 teeth',
    alt: 'Gradient wireframe mesh of a gear',
    note: 'Extruded trapezoidal tooth profile with a recessed web, six lightening holes, a raised hub and a keyed central bore — every wall carries its thickness.',
    specs: [['Teeth', '20 · root 0.86 / tip 1.00'], ['Faces', 'Rim ±0.14 · web ±0.07'], ['Bore', 'Ø 0.32 + keyway']],
  },
  {
    id: 'rocket',
    index: '03',
    name: 'Reusable Rocket',
    subtitle: 'Neutron-inspired',
    alt: 'Gradient wireframe mesh of a reusable rocket inspired by Neutron, with a tapered body, rounded split fairing and landing legs',
    note: 'Loosely based on the Neutron layout: a broad first stage tapering up to a rounded fairing with a split seam, and four legs it can stand on. It is an interpretation, not an engineering replica.',
    specs: [['Profile', 'Tapered body → rounded fairing'], ['Fairing', '2 halves · split seam'], ['Legs', '4 × strut + foot pad']],
  },
  {
    id: 'satellite',
    index: '04',
    name: 'Satellite',
    subtitle: 'Twin-array communications bus',
    alt: 'Fine blue-cyan-orange perspective wireframe of a satellite with two wide, symmetric, cell-gridded solar wings, a compact faceted bus and small reflector dish',
    note: 'Two long deployable wings extend from a chamfered avionics bus. Each wing has three extended frame modules carrying a nine-cell lattice, with rear spars, joint collars and triangulated boom mounts. A small offset reflector finishes the silhouette.',
    specs: [['Arrays', '2 wings · 3 extended modules each'], ['Cells', '54 bordered photovoltaic cells'], ['Structure', 'Extruded frames · booms · 8-facet bus']],
  },
] as const;

export default function Meshes() {
  useEffect(() => {
    const previousTitle = document.title;
    const title = 'Mesh Studies | Plasmics';
    document.title = title;
    const values: [string, string][] = [
      ['meta[name="description"]', 'Plasmics mesh studies: perspective wireframe sculptures of a road bike helmet, spur gear, reusable rocket and twin-array satellite, generated from mathematically defined geometry.'],
      ['meta[property="og:title"]', title],
      ['meta[property="og:description"]', 'Bike helmet, gear, reusable rocket and satellite as fine gradient-mesh sculpture studies.'],
    ];
    const previous = values.map(([selector, content]) => {
      const element = document.querySelector<HTMLMetaElement>(selector);
      const old = element?.content;
      if (element) element.content = content;
      return { element, old };
    });
    return () => {
      document.title = previousTitle;
      previous.forEach(({ element, old }) => { if (element && old !== undefined) element.content = old; });
    };
  }, []);

  return (
    <main className="w-full bg-background text-foreground pt-24 overflow-hidden">
      <section className="relative px-6 md:px-12 py-14 md:py-20 border-b border-border">
        <div className="container mx-auto max-w-6xl animate-in-fade">
          <Link href="/" data-testid="link-back-home" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground hover:text-primary transition-colors mb-10">
            <ArrowLeft className="w-3.5 h-3.5" /> Back home
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <span className="w-8 h-[1px] bg-primary" />
            <span className="font-mono text-primary text-xs font-bold tracking-[0.3em] uppercase">Sculpture Study · 04 Objects</span>
          </div>
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 md:gap-16 items-end">
            <h1 className="text-5xl md:text-7xl font-light tracking-tight leading-[1.02] text-balance">
              Mesh <span className="font-bold plasmics-gradient-text">studies.</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg font-light leading-relaxed">
              Four objects, each defined as parametric surfaces and edges, rotated in 3D and projected with perspective. Strands fade with depth. No fills, no renders — geometry only.
            </p>
          </div>
        </div>
      </section>

      {studies.map((s, i) => {
        const src = `${BASE}meshes/${s.id}.svg`;
        const flip = i % 2 === 1;
        return (
          <section key={s.id} id={s.id} data-testid={`section-mesh-${s.id}`} className="relative border-b border-border px-6 md:px-12 py-12 md:py-20">
            <div className={`container mx-auto max-w-6xl grid gap-8 md:gap-14 items-center ${flip ? 'md:grid-cols-[1fr_1.55fr] md:[&>*:first-child]:order-2' : 'md:grid-cols-[1.55fr_1fr]'}`}>
              <figure className="relative aspect-square w-full border border-border bg-muted/30">
                <span className="absolute left-3 top-3 w-3 h-3 border-l border-t border-foreground/30" />
                <span className="absolute right-3 top-3 w-3 h-3 border-r border-t border-foreground/30" />
                <span className="absolute left-3 bottom-3 w-3 h-3 border-l border-b border-foreground/30" />
                <span className="absolute right-3 bottom-3 w-3 h-3 border-r border-b border-foreground/30" />
                <span className="absolute left-5 top-4 font-mono text-[10px] tracking-[0.25em] text-muted-foreground">FIG. {s.index}</span>
                <img src={src} alt={s.alt} data-testid={`img-mesh-${s.id}`} className="absolute inset-0 w-full h-full object-contain p-4 md:p-8" loading={i === 0 ? 'eager' : 'lazy'} />
              </figure>
              <div className="flex flex-col gap-6">
                <div>
                  <div className="font-mono text-6xl md:text-7xl font-bold text-foreground/10 leading-none mb-3">{s.index}</div>
                  <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{s.name}</h2>
                  <p className="font-mono text-xs tracking-[0.2em] uppercase text-secondary mt-2">{s.subtitle}</p>
                </div>
                <p className="text-muted-foreground font-light leading-relaxed">{s.note}</p>
                <dl className="border-t border-border">
                  {s.specs.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 py-2.5 border-b border-border text-sm">
                      <dt className="font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground">{k}</dt>
                      <dd className="font-mono text-xs text-foreground text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <a href={src} download={`plasmics-${s.id}.svg`} data-testid={`button-download-${s.id}`} className="self-start inline-flex items-center gap-3 bg-foreground text-background px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase hover:bg-primary transition-colors">
                  <Download className="w-4 h-4" /> Download SVG
                </a>
              </div>
            </div>
          </section>
        );
      })}

      <section className="px-6 md:px-12 py-14">
        <div className="container mx-auto max-w-6xl flex flex-col md:flex-row md:items-center justify-between gap-6">
          <p className="font-mono text-xs text-muted-foreground tracking-wide">Source: scripts/generate-mesh-assets.mjs — deterministic, reproducible.</p>
          <Link href="/" data-testid="link-back-home-footer" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-foreground hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" /> Return to Plasmics
          </Link>
        </div>
      </section>
    </main>
  );
}
